import type { CSMTheme } from '../../../types';
import { getContrastYIQ } from '../get-contrast';

type ColorRamps = CSMTheme['colorRamps'];
type Ramp = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 1000;

export type BrandDerivation = {
	brand: string;
	hover: string;
	active: string;
	brandText: string;
	hoverText: string;
	activeText: string;
};

const clampRamp = (n: number): Ramp => {
	const v = Math.max(100, Math.min(1000, n));
	return v as Ramp;
};

const buildRampMap = (r: ColorRamps) =>
	new Map<number, string>([
		[100, r.color100],
		[200, r.color200],
		[300, r.color300],
		[400, r.color400],
		[500, r.color500],
		[600, r.color600],
		[700, r.color700],
		[800, r.color800],
		[900, r.color900],
		[1000, r.color1000],
	]);

const textOn = (hex: string) => (getContrastYIQ(hex) === 'dark' ? '#FFFFFF' : '#000000');

/**
 * Derives brand background and text colors (default, hover, active) from a generated color ramp.
 *
 * How it works:
 * - Base ramp: Finds the exact ramp step (100–1000) whose hex equals the provided primaryColor.
 * - Interaction ramps:
 *   - If base >= 800 (very dark end), move lighter for better contrast:
 *     - hover = base - 200
 *     - active = base - 300
 *   - Otherwise, move darker:
 *     - hover = base + 200
 *     - active = base + 300
 *   - All ramp values are clamped to [100, 1000].
 * - Text colors: For each background (base/hover/active), uses YIQ contrast to choose
 *   '#FFFFFF' on dark backgrounds and '#000000' on light backgrounds.
 *
 *
 * @param ramps - The color ramps to use.
 * @param primaryColor - The primary color to derive from (guaranteed to be present in ramps).
 * @returns The derived brand colors, including brand, hover, active, brandText, hoverText, and activeText.
 */
export const deriveBrandColors = (ramps: ColorRamps, primaryColor: string): BrandDerivation => {
	const rampMap = buildRampMap(ramps);

	const target = primaryColor.toLowerCase();
	let baseRamp: Ramp = 400;
	for (const [n, hex] of rampMap) {
		if (hex.toLowerCase() === target) {
			baseRamp = n as Ramp;
			break;
		}
	}

	const goLighter = baseRamp >= 800;
	const hoverRamp = clampRamp(baseRamp + (goLighter ? -200 : 200));
	const activeRamp = clampRamp(baseRamp + (goLighter ? -300 : 300));

	const brand = rampMap.get(baseRamp)!;
	const hover = rampMap.get(hoverRamp)!;
	const active = rampMap.get(activeRamp)!;

	return {
		brand,
		hover,
		active,
		brandText: textOn(brand),
		hoverText: textOn(hover),
		activeText: textOn(active),
	};
};
